import { useEffect, useState, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { websocketService } from '@/lib/websocket-service';
import { getVideoUrl, getModuleHeroUrl, getDynamicImageUrl } from '@/lib/media';
import ManagedVideo from '@/components/common/ManagedVideo';
import type { BackgroundRoomContext } from '@danda/shared';

// Default lobby background video (pre-generated seamless loop)
// Falls back to static image if video not available
const DEFAULT_LOBBY_VIDEO = getVideoUrl('tavian.mp4');
// Fallback image for lobby background
const DEFAULT_LOBBY_IMAGE = getModuleHeroUrl('lobby');

interface MediaState {
  url: string;
  type: 'video' | 'image';
}

interface LobbyBackgroundProps {
  /** Room context for generating dynamic background */
  roomContext?: BackgroundRoomContext;
}

/**
 * Full-screen lobby background component supporting both video and image
 *
 * Features:
 * - Uses pre-generated seamless loop videos for default lobby and NPC chats
 * - Falls back to dynamic image generation for party chat and N:N conversations
 * - Videos play full-screen, auto-loop, without playback controls
 * - Covers full screen without distortion (object-cover)
 * - Semi-transparent with theme-aware overlay for text legibility
 * - Smooth crossfade transitions between media
 */
export default function LobbyBackground({ roomContext }: LobbyBackgroundProps) {
  // Get background state from store
  const backgroundMediaUrl = useLobbyStore((s) => s.backgroundMediaUrl);
  const backgroundMediaType = useLobbyStore((s) => s.backgroundMediaType);
  // Used by the dedup check: when a local-only effect (PRIVATE/INNER) writes
  // the bg with `null` context, returning to a previously-fetched server-side
  // context would otherwise short-circuit on `lastContextRef` and leave the
  // private portrait stuck. Treat a context mismatch as "needs refetch".
  const backgroundRoomContext = useLobbyStore((s) => s.backgroundRoomContext);

  // Current displayed media (stays visible during loading)
  const [currentMedia, setCurrentMedia] = useState<MediaState>({
    url: DEFAULT_LOBBY_VIDEO,
    type: 'video',
  });
  // Next media being loaded (for crossfade)
  const [nextMedia, setNextMedia] = useState<MediaState | null>(null);
  const [isNextMediaLoaded, setIsNextMediaLoaded] = useState(false);
  // Track if default video failed to load (fallback to image)
  const [defaultVideoFailed, setDefaultVideoFailed] = useState(false);

  // Track the last context to avoid unnecessary regeneration
  const lastContextRef = useRef<string>('');
  // Track the most recent nextMedia to prevent race conditions during crossfade
  const nextMediaRef = useRef<MediaState | null>(null);

  // Preload image helper
  const preloadImage = useCallback((url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  // Build full URL from relative path - all media from GCS bucket
  const buildFullUrl = useCallback((url: string): string => {
    // Full URLs (GCS) - return as-is
    if (url.startsWith('http')) return url;
    // Dynamic images are served from GCS
    return getDynamicImageUrl(url);
  }, []);

  // Request background when room context changes
  useEffect(() => {
    if (!roomContext) return;

    // Create a context key for comparison
    const contextKey = JSON.stringify(roomContext);
    const storeKey = backgroundRoomContext ? JSON.stringify(backgroundRoomContext) : null;

    // Skip only if BOTH the requested context hasn't changed AND the store
    // already reflects that same context. If a local-only effect (PRIVATE /
    // INNER) cleared/replaced the store context in between, force a refetch
    // so the prior portrait doesn't stick.
    if (contextKey === lastContextRef.current && contextKey === storeKey) {
      return;
    }
    lastContextRef.current = contextKey;

    // Request new background from server
    // Server decides whether to return pre-generated video or trigger dynamic generation
    websocketService.requestBackground(roomContext);
  }, [roomContext, backgroundRoomContext]);

  // Keep nextMediaRef in sync with nextMedia state
  useEffect(() => {
    nextMediaRef.current = nextMedia;
  }, [nextMedia]);

  // Update media when new background is received from server
  useEffect(() => {
    if (backgroundMediaUrl && backgroundMediaType) {
      const fullUrl = buildFullUrl(backgroundMediaUrl);
      const newMedia = {
        url: fullUrl,
        type: backgroundMediaType,
      };
      setNextMedia(newMedia);
      setIsNextMediaLoaded(false);
    }
  }, [backgroundMediaUrl, backgroundMediaType, buildFullUrl]);

  // Preload the default fallback image on mount
  useEffect(() => {
    preloadImage(DEFAULT_LOBBY_IMAGE).catch(() => {
      console.warn('Failed to preload default lobby background image');
    });
  }, [preloadImage]);

  // Handle crossfade when next media is ready (with race condition protection)
  useEffect(() => {
    if (nextMedia && isNextMediaLoaded) {
      const mediaToApply = nextMedia;
      // Swap media after a brief delay for smooth transition
      const timer = setTimeout(() => {
        // Only apply if this is still the most recent media (prevents race condition)
        if (nextMediaRef.current === mediaToApply) {
          setCurrentMedia(mediaToApply);
          setNextMedia(null);
          setIsNextMediaLoaded(false);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [nextMedia, isNextMediaLoaded]);

  // Handle default video load failure - fall back to image
  const handleDefaultVideoError = useCallback(() => {
    if (!defaultVideoFailed) {
      setDefaultVideoFailed(true);
      setCurrentMedia({
        url: DEFAULT_LOBBY_IMAGE,
        type: 'image',
      });
    }
  }, [defaultVideoFailed]);

  // Render video element — ManagedVideo handles muted state, autoplay, and cleanup
  const renderVideo = (
    url: string,
    isHidden: boolean,
    onCanPlay?: () => void,
    onError?: () => void,
    onAutoplayBlocked?: () => void
  ) => (
    <ManagedVideo
      src={url}
      className={cn(
        'fixed inset-0 w-full h-full object-cover z-0 transition-opacity duration-700',
        isHidden && 'opacity-0 pointer-events-none'
      )}
      onCanPlay={onCanPlay}
      onError={onError}
      onAutoplayBlocked={onAutoplayBlocked}
    />
  );

  // Render image element (as background div for consistency)
  const renderImage = (url: string, isHidden: boolean) => (
    <div
      className={cn(
        'fixed inset-0 z-0 transition-opacity duration-700',
        isHidden && 'opacity-0 pointer-events-none'
      )}
      style={{
        backgroundImage: `url(${url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top',
        backgroundRepeat: 'no-repeat',
      }}
    />
  );

  return (
    <>
      {/* Current background media layer */}
      {currentMedia.type === 'video'
        ? renderVideo(
            currentMedia.url,
            false,
            undefined,
            handleDefaultVideoError,
            // On autoplay block, fall back to static image for default video
            currentMedia.url === DEFAULT_LOBBY_VIDEO ? handleDefaultVideoError : undefined
          )
        : renderImage(currentMedia.url, false)}

      {/* Next media (for crossfade) - hidden until loaded */}
      {nextMedia &&
        (nextMedia.type === 'video' ? (
          renderVideo(
            nextMedia.url,
            !isNextMediaLoaded,
            () => setIsNextMediaLoaded(true),
            () => {
              // On video load error, clear next media and fall back
              console.warn('Failed to load video, keeping current background');
              setNextMedia(null);
              setIsNextMediaLoaded(false);
            }
          )
        ) : (
          <>
            {/* Hidden preloader for images */}
            <img
              src={nextMedia.url}
              alt=""
              className="hidden"
              onLoad={() => setIsNextMediaLoaded(true)}
              onError={() => {
                console.warn('Failed to load image, keeping current background');
                setNextMedia(null);
                setIsNextMediaLoaded(false);
              }}
            />
            {/* Actual image layer (shown after preload) */}
            {isNextMediaLoaded && renderImage(nextMedia.url, false)}
          </>
        ))}

      {/* Theme-aware overlay for text legibility - lighter to show more background */}
      {/* Light mode: subtle white overlay, Dark mode: subtle dark overlay */}
      <div
        className={cn(
          'fixed inset-0 z-0 pointer-events-none',
          // Lighter overlay to let more background show through
          'bg-gradient-to-b from-background/40 via-background/20 to-background/30'
        )}
      />
    </>
  );
}
